import React, { useState } from 'react'
import styled, { css } from 'styled-components'


export type InputProps = React.InputHTMLAttributes<HTMLInputElement> & {
    label?: string
	className?: string
    error?: string | null
}


// Label has to be below RawInput to target the input when focused and update its color 
export const InputLabel: React.FunctionComponent<InputProps> = ({label, className, error, children, ...props }) => {
	const [showPassword, setShowPassword] = useState<boolean>(false)
	
	const isPasswordInput = props.type === "password"

	const type = isPasswordInput
		? showPassword
			? "text"
			: "password"
		: props.type

	return (
		<Container className={className}>
			{
				error
				? <Error>{error}</Error>
				: null
			}
			<RawInput
				{...props}
				type={type}
				error={error}
			/>
			{ label && <Label error={error}>{ label } </Label> }
			{
			isPasswordInput &&
				<Icon 
					onClick={ev => setShowPassword(!showPassword)} 
					src={showPassword ? require('./eye.svg') :require('./eye-slashed.svg')} 
				/>
			}
			{children}
		</Container>
	)
}


const Container = styled.div<{error?: string | null}>`
    position: relative;
	display: flex;

	padding: 13px 20px;
	margin: 10px 0 30px 0;

	/* All visual input properties should be set on Container so they can be changed from outside with className */
	background: ${p => p.theme.colors.bg};

	border-radius: 8px;
	border: 1px solid ${p => p.error
								? p.theme.colors.error
								: p.theme.colors.lightGray};

	&:focus {
		border: 1px solid ${p => p.error
									? p.theme.colors.error
									: p.theme.colors.accent};
    }

	overflow: visible;
`

// The Montserrat font has really small privacy dots so we need to use another (large Verdana)
// The font property is quite verbose but it seems the only way to format
// the placeholder and the input with different fonts
const RawInput = styled.input<{error?: string | null, type?: string }>`
    width: 100%;

	/*
	@zuldare: The input "jumps" when you press the eye icon if done like this
	font: ${p => !p.value 
					? '14px Montserrat'
					: p.type === 'password'
						? 'large Verdana'
						: '14px Montserrat'}; 
	*/
	font-size: 14px;
	line-height: 20px;

	::placeholder,
	::-webkit-input-placeholder {
		opacity: 0.5;
	}
	:-ms-input-placeholder {
		opacity: 0.5;
	}

    &, &:active {
		outline: none;
		border: none;
	}
`
// The last property is long and quite hard to read but I haven't found a better
// way to do it. 	
const Label = styled.label<{error?: string | null}>`
	position: absolute;
	top: -10px;
	left: 10px;

	background: ${p => p.theme.colors.bg};
	padding: 3px;

	font-size: 12px;
	color: ${p => p.error 
					? p.theme.colors.error 
					: p.theme.colors.shadeGray};
	${p => p.error === null && css`
		${RawInput}:focus + & {
			color: ${p.theme.colors.accent};
		}`
	}
`
	
const Error = styled.div`
    position: absolute;
    top: 100%;
    left: 10px;

    padding: 3px 5px;
    font-size: 12px;
    color: ${p => p.theme.colors.error};
`

const Icon = styled.img`
	width: 20px;
	height: 20px;
	position: absolute;
	top: 13px;
	right: 15px;

	cursor: pointer;
`
